// Environment capture utility for issue submission
// Collects browser, app, and Kepler.gl metadata for bug reports
// SPDX-License-Identifier: MIT

import { getAppVersion } from './version';
import store from '../store';

// --- Types ---

export interface BrowserMetadata {
  userAgent: string;
  viewportWidth: number;
  viewportHeight: number;
  language: string;
  platform: string;
}

export interface AppMetadata {
  version: string;
  mode: string;
  url: string;
}

export interface KeplerMetadata {
  datasetCount: number;
  layerCount: number;
  filterCount: number;
  mapCenter: { latitude: number; longitude: number } | null;
  mapZoom: number | null;
}

export interface EnvironmentMetadata {
  browser: BrowserMetadata;
  app: AppMetadata;
  kepler: KeplerMetadata;
  timestamp: string;
}

// --- Sensitive data patterns to exclude ---

const SENSITIVE_PATTERNS = [
  /VITE_MAPBOX_TOKEN/i,
  /mapbox.*token/i,
  /sig=[^&]*/i, // SAS token signatures
  /sv=[^&]*/i, // SAS token versions
  /\.blob\.core\.windows\.net/i,
  /\.dfs\.core\.windows\.net/i,
  /ghp_[a-zA-Z0-9]+/i, // GitHub PATs
  /Bearer\s+[a-zA-Z0-9._-]+/i,
  /sk-[a-zA-Z0-9]+/i, // API keys
];

/**
 * Check if a string contains sensitive data
 */
function containsSensitiveData(value: string): boolean {
  return SENSITIVE_PATTERNS.some(pattern => pattern.test(value));
}

/**
 * Sanitize a metadata value — return '[REDACTED]' if it contains sensitive data
 */
function sanitizeValue(value: string): string {
  if (containsSensitiveData(value)) {
    return '[REDACTED]';
  }
  return value;
}

// --- Browser metadata collection ---

export function captureBrowserMetadata(): BrowserMetadata {
  return {
    userAgent: navigator.userAgent,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    language: navigator.language,
    platform: navigator.platform || 'unknown',
  };
}

// --- App metadata collection ---

export function captureAppMetadata(): AppMetadata {
  // Only capture origin + pathname, no query params (could contain tokens)
  const url = `${window.location.origin}${window.location.pathname}`;
  return {
    version: getAppVersion(),
    mode: import.meta.env.MODE || 'unknown',
    url: sanitizeValue(url),
  };
}

// --- Kepler.gl state summary ---

export function captureKeplerMetadata(): KeplerMetadata {
  const state = store.getState() as any;
  const keplerState = state.keplerGl?.map;

  if (!keplerState) {
    return {
      datasetCount: 0,
      layerCount: 0,
      filterCount: 0,
      mapCenter: null,
      mapZoom: null,
    };
  }

  const visState = keplerState.visState;
  const mapState = keplerState.mapState;

  return {
    datasetCount: visState?.datasets ? Object.keys(visState.datasets).length : 0,
    layerCount: visState?.layers ? visState.layers.length : 0,
    filterCount: visState?.filters ? visState.filters.length : 0,
    mapCenter:
      mapState?.latitude != null && mapState?.longitude != null
        ? { latitude: mapState.latitude, longitude: mapState.longitude }
        : null,
    mapZoom: mapState?.zoom ?? null,
  };
}

// --- Main capture function ---

/**
 * Capture full environment metadata for issue submission.
 * Collects browser, app, and Kepler.gl state summary.
 * Sensitive data (tokens, SAS, blob URLs) is excluded.
 */
export function captureEnvironment(): EnvironmentMetadata {
  return {
    browser: captureBrowserMetadata(),
    app: captureAppMetadata(),
    kepler: captureKeplerMetadata(),
    timestamp: new Date().toISOString(),
  };
}

// --- Markdown formatting ---

/**
 * Format environment metadata as a collapsible Markdown `<details>` block
 * with a summary table, suitable for embedding in GitHub issue bodies.
 */
export function formatEnvironmentMarkdown(env: EnvironmentMetadata): string {
  const rows: string[] = [];

  // Browser
  rows.push(`| User Agent | ${sanitizeValue(env.browser.userAgent)} |`);
  rows.push(`| Viewport | ${env.browser.viewportWidth} × ${env.browser.viewportHeight} |`);
  rows.push(`| Language | ${env.browser.language} |`);
  rows.push(`| Platform | ${env.browser.platform} |`);

  // App
  rows.push(`| App Version | ${sanitizeValue(env.app.version)} |`);
  rows.push(`| Mode | ${env.app.mode} |`);
  rows.push(`| URL | ${sanitizeValue(env.app.url)} |`);

  // Kepler
  rows.push(`| Datasets | ${env.kepler.datasetCount} |`);
  rows.push(`| Layers | ${env.kepler.layerCount} |`);
  rows.push(`| Filters | ${env.kepler.filterCount} |`);

  if (env.kepler.mapCenter) {
    rows.push(
      `| Map Center | ${env.kepler.mapCenter.latitude.toFixed(4)}, ${env.kepler.mapCenter.longitude.toFixed(4)} |`
    );
  }
  if (env.kepler.mapZoom != null) {
    rows.push(`| Map Zoom | ${env.kepler.mapZoom.toFixed(2)} |`);
  }

  rows.push(`| Timestamp | ${env.timestamp} |`);

  // Build the final markdown: validation — ensure no sensitive data leaked into output
  const tableContent = rows.join('\n');
  if (containsSensitiveData(tableContent)) {
    // This should never happen if sanitizeValue works correctly, but defense in depth
    return '<details><summary>Environment</summary>\n\n[Environment data redacted due to sensitive content detection]\n\n</details>';
  }

  return [
    '<details><summary>Environment</summary>',
    '',
    '| Property | Value |',
    '| --- | --- |',
    tableContent,
    '',
    '</details>',
  ].join('\n');
}
